import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getReportApiBaseUrl } from '../config/api';
import {
  getLiveAccessConfig,
  getLiveAccessApplicationReviewDetails,
  getLiveHealthOverview,
  getLiveHealthSession,
  getTopReporters,
  listLiveAccessApplications,
  listLiveSessions,
  listLiveHistory,
  listReportedLiveSessions,
  listUserLiveSessionsWithReports,
  reviewLiveAccessApplication,
  updateLiveAccessConfig,
  LiveAccessApplication,
  LiveAccessApplicationStatus,
  LiveAccessConfig,
  LiveAccessMode,
  LiveHealthOverview,
  LiveHealthSessionDetail,
  LiveHealthSessionMetadata,
  LiveReportedSessionSummary,
  LiveSession,
  LiveSessionPublishStatus,
  TopReporter,
} from '../services/liveAccessApi';

type TabType = 'applications' | 'config' | 'streams' | 'reports' | 'analytics' | 'health';
type StatusFilter = 'ALL' | LiveAccessApplicationStatus;
type StreamPublishFilter = 'ALL' | LiveSessionPublishStatus;

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ApplicationStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  blocked: number;
  suspended: number;
}

interface StreamRow {
  sessionId: string;
  title: string;
  hostUserId: string;
  hostName?: string | null;
  coverUrl?: string | null;
  lifecycleStatus: string;
  publishStatus?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  reportCount?: number;
  reporterCount?: number;
}

const defaultApplicationsPagination: PaginationState = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
};

const defaultBrowsePagination: PaginationState = {
  page: 1,
  limit: 12,
  total: 0,
  totalPages: 1,
};

const defaultReportPagination: PaginationState = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
};

const defaultStats: ApplicationStats = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  blocked: 0,
  suspended: 0,
};

const tabPaths: Record<TabType, string> = {
  applications: '/live-access',
  config: '/live-access/config',
  streams: '/live-access/streams',
  reports: '/live-access/reports',
  analytics: '/live-access/analytics',
  health: '/live-access/health',
};

const normalizeUserIds = (userIds: string[]) => {
  return Array.from(
    new Set(
      userIds
        .map((userId) => userId.trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));
};

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const hostDisplayName = (metadata?: LiveHealthSessionMetadata | null) => {
  const host = metadata?.host;
  return host?.name || host?.userName || host?.email || metadata?.hostUserId || 'host unknown';
};

const hostSubline = (metadata?: LiveHealthSessionMetadata | null) => {
  const host = metadata?.host;
  if (host?.name && host?.userName) return `@${host.userName}`;
  return host?.email || null;
};

const hostAvatarUrl = (metadata?: LiveHealthSessionMetadata | null) => {
  const host = metadata?.host;
  return host?.profileImageThumbnailUrl || host?.profileImageUrl || '';
};

const hostPresenceLabel = (
  hostHeartbeat: boolean,
  hostHeartbeatTtl: number,
  inactiveAt?: string | null,
  metadataInactiveAt?: string | null,
) => {
  if (hostHeartbeat) return `heartbeat ttl ${hostHeartbeatTtl}s`;
  const inactiveValue = inactiveAt || metadataInactiveAt;
  if (inactiveValue) return `inactive since ${formatDate(inactiveValue)}`;
  return 'no heartbeat marker';
};

const renderHealthHost = (
  metadata: LiveHealthSessionMetadata | null | undefined,
  hostHeartbeat: boolean,
  hostHeartbeatTtl: number,
  inactiveAt?: string | null,
) => {
  const displayName = hostDisplayName(metadata);
  const avatarUrl = hostAvatarUrl(metadata);
  const subline = hostSubline(metadata);
  return (
    <div className='flex min-w-[240px] items-center gap-3'>
      {avatarUrl ? (
        <img src={avatarUrl} alt='' className='h-9 w-9 rounded-full object-cover' />
      ) : (
        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600'>
          {displayName.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div>
        <div className={hostHeartbeat ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}>
          {displayName}
        </div>
        {subline ? <div className='text-xs text-gray-500'>{subline}</div> : null}
        <div className='font-mono text-[11px] text-gray-400'>{metadata?.hostUserId || 'unknown'}</div>
        <div className={hostHeartbeat ? 'text-xs text-emerald-600' : 'text-xs text-amber-700'}>
          {hostPresenceLabel(hostHeartbeat, hostHeartbeatTtl, inactiveAt, metadata?.inactiveAt)}
        </div>
      </div>
    </div>
  );
};

const formatTelemetryValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'N/A';
  return String(value);
};

const renderTelemetryDetails = (details?: Record<string, unknown>) => {
  if (!details || Object.keys(details).length === 0) return <span className='text-gray-400'>No details</span>;

  const priorityKeys = [
    'mode',
    'connectionState',
    'socketSubscribed',
    'remoteParticipantCount',
    'roomState',
    'reason',
    'willRetry',
    'attempt',
    'maxAttempts',
    'nextDelayMs',
    'error',
  ];
  const visibleEntries = priorityKeys
    .filter((key) => Object.prototype.hasOwnProperty.call(details, key))
    .map((key) => [key, details[key]] as const);
  const extraEntries = Object.entries(details).filter(([key]) => !priorityKeys.includes(key));

  return (
    <div className='flex min-w-[260px] flex-wrap gap-1.5'>
      {visibleEntries.map(([key, value]) => (
        <span key={key} className='rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-700'>
          <span className='font-semibold'>{key}:</span> {formatTelemetryValue(value)}
        </span>
      ))}
      {extraEntries.length ? (
        <span className='rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-500'>
          extra: {JSON.stringify(Object.fromEntries(extraEntries))}
        </span>
      ) : null}
    </div>
  );
};

const getStatusBadgeClass = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  if (status === 'BLOCKED') return 'bg-orange-100 text-orange-700 border-orange-200';
  if (status === 'SUSPENDED') return 'bg-red-900 text-red-100 border-red-800';
  if (status === 'LIVE') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'STARTING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'PUBLISHED') return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const toStreamRowFromLiveSession = (session: LiveSession): StreamRow => ({
  sessionId: session.SessionID,
  title: session.Title || 'Untitled live',
  hostUserId: session.HostUserID,
  hostName: session.HostUserID,
  coverUrl: session.CoverUrl || null,
  lifecycleStatus: session.LifecycleStatus,
  publishStatus: session.PublishStatus || null,
  startedAt: session.StartedAt || null,
  endedAt: session.EndedAt || null,
});

const toStreamRowFromReportedSummary = (session: LiveReportedSessionSummary): StreamRow => ({
  sessionId: session.sessionId,
  title: session.sessionTitle || 'Untitled live',
  hostUserId: session.hostUserId,
  hostName: session.host?.Name || session.host?.UserName || session.hostUserId,
  coverUrl: session.coverUrl || null,
  lifecycleStatus: session.sessionLifecycleStatus,
  startedAt: session.startedAt || null,
  endedAt: session.endedAt || null,
  reportCount: session.reportCount,
  reporterCount: session.reporterCount,
});

const SummaryCard: React.FC<{ label: string; value: React.ReactNode; tone?: string }> = ({
  label,
  value,
  tone = 'text-gray-900',
}) => (
  <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
    <p className='text-sm text-gray-500'>{label}</p>
    <p className={`mt-1 text-3xl font-bold ${tone}`}>{value}</p>
  </div>
);

type AnalyticsSortKey = 'recent' | 'views' | 'likes' | 'notifications' | 'username';

const LivestreamAnalyticsTab: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [sortKey, setSortKey] = useState<AnalyticsSortKey>('recent');
  const LIMIT = 12;

  const load = useCallback(async (p: number, filter: string) => {
    setLoading(true);
    try {
      const resp = await listLiveHistory({ page: p, limit: LIMIT, ...(filter ? { hostUserId: filter } : {}) });
      setSessions(resp.sessions);
      setTotal(resp.total);
      setTotalPages(Math.max(1, Math.ceil(resp.total / LIMIT)));
    } catch (err: any) {
      toast.error(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1, ''); }, [load]);

  const applyFilter = () => {
    const trimmed = searchInput.trim();
    setActiveFilter(trimmed);
    setPage(1);
    load(1, trimmed);
  };

  const clearFilter = () => {
    setSearchInput('');
    setActiveFilter('');
    setPage(1);
    load(1, '');
  };

  const goToPage = (p: number) => { setPage(p); load(p, activeFilter); };

  const sorted = useMemo(() => {
    const s = [...sessions];
    if (sortKey === 'views') return s.sort((a, b) => (b.Stats?.TotalUniqueViewers ?? 0) - (a.Stats?.TotalUniqueViewers ?? 0));
    if (sortKey === 'likes') return s.sort((a, b) => (b.Stats?.TotalLikes ?? 0) - (a.Stats?.TotalLikes ?? 0));
    if (sortKey === 'notifications') return s.sort((a, b) => (b.NotificationStats?.totalSent ?? 0) - (a.NotificationStats?.totalSent ?? 0));
    if (sortKey === 'username') return s.sort((a, b) => {
      const nameA = (a.host?.UserName || a.host?.Name || '').toLowerCase();
      const nameB = (b.host?.UserName || b.host?.Name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return s; // 'recent' — backend already sorted by EndedAt desc
  }, [sessions, sortKey]);

  const sortOptions: { key: AnalyticsSortKey; label: string }[] = [
    { key: 'recent', label: 'Most Recent' },
    { key: 'views', label: 'Most Views' },
    { key: 'likes', label: 'Most Likes' },
    { key: 'notifications', label: 'Most Notified' },
    { key: 'username', label: 'Username A–Z' },
  ];

  return (
    <div className='space-y-6'>
      <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
        {/* Header + controls */}
        <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 lg:flex-row lg:items-center lg:justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Livestream Analytics</h2>
            <p className='text-sm text-gray-500'>Click any session to view full analytics.</p>
          </div>
          <div className='flex flex-wrap gap-2 items-center'>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilter(); }}
              placeholder='Search by username or user ID'
              className='rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none w-56'
            />
            <button onClick={applyFilter} className='rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'>
              Apply
            </button>
            {activeFilter && (
              <button onClick={clearFilter} className='rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'>
                Clear
              </button>
            )}
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as AnalyticsSortKey)}
              className='rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
            >
              {sortOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className='px-5 py-16 text-center text-sm text-gray-500'>Loading sessions...</div>
        ) : sorted.length === 0 ? (
          <div className='px-5 py-16 text-center text-sm text-gray-500'>No sessions found.</div>
        ) : (
          <div className='grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3'>
            {sorted.map((session) => {
              const views = session.Stats?.TotalUniqueViewers ?? 0;
              const likes = session.Stats?.TotalLikes ?? 0;
              const notifSent = session.NotificationStats?.totalSent ?? 0;
              const hostName = session.host?.Name || null;
              const hostUsername = session.host?.UserName ? `@${session.host.UserName}` : null;
              const hostDisplay = hostName || hostUsername || session.HostUserID;
              const hostSub = hostName && hostUsername ? hostUsername : null;

              return (
                <button
                  key={session.SessionID}
                  onClick={() => navigate(`/live-access/analytics/${encodeURIComponent(session.SessionID)}`)}
                  className='group text-left overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:border-orange-300 hover:shadow-md'
                >
                  {/* Cover */}
                  <div className='relative h-36 w-full bg-gradient-to-br from-gray-100 to-gray-200'>
                    {session.CoverUrl ? (
                      <img src={session.CoverUrl} alt={session.Title} className='h-full w-full object-cover' />
                    ) : (
                      <div className='flex h-full items-center justify-center text-sm text-gray-400'>No cover</div>
                    )}
                    <div className='absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10'>
                      <span className='scale-90 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold text-gray-900 opacity-0 shadow transition-all group-hover:scale-100 group-hover:opacity-100'>
                        View Analytics
                      </span>
                    </div>
                    <div className='absolute top-2 left-2 flex flex-wrap gap-1'>
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold backdrop-blur-sm ${
                        session.LifecycleStatus === 'LIVE' ? 'border-emerald-300 bg-emerald-100/90 text-emerald-800' :
                        session.LifecycleStatus === 'ENDED' ? 'border-gray-300 bg-white/90 text-gray-700' :
                        'border-amber-300 bg-amber-100/90 text-amber-800'
                      }`}>{session.LifecycleStatus}</span>
                      {session.PublishStatus && (
                        <span className='inline-flex rounded-full border border-blue-300 bg-blue-100/90 px-2 py-0.5 text-xs font-semibold text-blue-800 backdrop-blur-sm'>
                          {session.PublishStatus}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className='p-3'>
                    {/* Title */}
                    <p className='truncate font-semibold text-gray-900 group-hover:text-orange-600 transition-colors text-sm'>
                      {session.Title || 'Untitled live'}
                    </p>

                    {/* Host */}
                    <div className='mt-1 flex items-center gap-1.5'>
                      <span className='text-xs font-medium text-gray-700 truncate'>{hostDisplay}</span>
                      {hostSub && <span className='text-xs text-gray-400 truncate'>{hostSub}</span>}
                    </div>

                    {/* Metric chips */}
                    <div className='mt-2.5 flex flex-wrap gap-1.5'>
                      <span className='inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700'>
                        👁 {views.toLocaleString()}
                      </span>
                      <span className='inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-600'>
                        ❤️ {likes.toLocaleString()}
                      </span>
                      <span className='inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700'>
                        🔔 {notifSent.toLocaleString()}
                      </span>
                    </div>

                    {/* Dates */}
                    <div className='mt-2 grid grid-cols-2 gap-1 text-xs text-gray-500'>
                      <div>
                        <span className='text-gray-400'>Started</span>
                        <p className='font-medium text-gray-700'>{formatDate(session.StartedAt)}</p>
                      </div>
                      <div>
                        <span className='text-gray-400'>Ended</span>
                        <p className='font-medium text-gray-700'>{formatDate(session.EndedAt)}</p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
          <p className='text-sm text-gray-600'>Page {page} of {totalPages} · {total} sessions</p>
          <div className='flex items-center gap-2'>
            <button onClick={() => goToPage(Math.max(1, page - 1))} disabled={page <= 1 || loading}
              className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'>
              Previous
            </button>
            <button onClick={() => goToPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages || loading}
              className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LiveHealthTab: React.FC = () => {
  const [overview, setOverview] = useState<LiveHealthOverview | null>(null);
  const [sessionDetail, setSessionDetail] = useState<LiveHealthSessionDetail | null>(null);
  const [sessionInput, setSessionInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const reportApiBaseUrl = getReportApiBaseUrl();

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await getLiveHealthOverview());
      setOverviewError(null);
    } catch (error: any) {
      const message = error.message || 'Failed to load live health';
      setOverviewError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (sessionId: string) => {
    const trimmed = sessionId.trim();
    if (!trimmed) return;
    setSessionLoading(true);
    try {
      setSessionDetail(await getLiveHealthSession(trimmed));
      setSessionInput(trimmed);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load session health');
    } finally {
      setSessionLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
    const timer = window.setInterval(loadOverview, 10000);
    return () => window.clearInterval(timer);
  }, [loadOverview]);

  const healthyServices = overview?.services.filter((service) => service.healthy).length || 0;
  const totalServices = overview?.services.length || 0;
  const redAlerts = overview?.alerts.filter((alert) => alert.severity === 'red').length || 0;

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-4'>
        <SummaryCard label='Service health' value={`${healthyServices}/${totalServices}`} tone='text-gray-900' />
        <SummaryCard label='Active sessions' value={overview?.activeSessions.total ?? 0} tone='text-gray-900' />
        <SummaryCard
          label='Live stream lag'
          value={overview?.streamLag.available ? overview.streamLag.lag ?? 'unknown' : 'unavailable'}
          tone='text-gray-900'
        />
        <SummaryCard label='Red alerts' value={redAlerts} tone={redAlerts > 0 ? 'text-red-600' : 'text-emerald-600'} />
      </div>

      <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
        <div className='flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Livestream System Health</h2>
            <p className='text-sm text-gray-500'>Auto-refreshes every 10 seconds. Last update: {formatDate(overview?.generatedAt)}</p>
            <p className='mt-1 font-mono text-xs text-gray-400'>API: {reportApiBaseUrl}/live-health/overview</p>
          </div>
          <button
            onClick={loadOverview}
            disabled={loading}
            className='rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50'
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {overviewError ? (
          <div className='border-b border-red-100 bg-red-50 px-5 py-4 text-sm text-red-700'>
            <div className='font-semibold'>Live health API is not reachable from this browser.</div>
            <div className='mt-1'>{overviewError}</div>
            <div className='mt-1 text-red-600'>
              If DevTools mentions CORS and the <span className='font-mono'>userid</span> header,
              deploy the report API CORS update and refresh this page.
            </div>
          </div>
        ) : null}

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Service</th>
                <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Namespace</th>
                <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Replicas</th>
                <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Pods</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white'>
              {(overview?.services || []).map((service) => (
                <tr key={`${service.namespace}:${service.deployment}`}>
                  <td className='px-5 py-4'>
                    <div className='font-semibold text-gray-900'>{service.name}</div>
                    <div className={service.healthy ? 'text-sm text-emerald-600' : 'text-sm text-red-600'}>
                      {service.available ? (service.healthy ? 'Healthy' : 'Unhealthy') : service.error || 'Unavailable'}
                    </div>
                  </td>
                  <td className='px-5 py-4 text-sm text-gray-600'>{service.namespace}</td>
                  <td className='px-5 py-4 text-sm text-gray-700'>
                    {service.replicas ? `${service.replicas.available}/${service.replicas.desired} available` : 'N/A'}
                  </td>
                  <td className='px-5 py-4 text-sm text-gray-700'>
                    {(service.pods || []).length === 0 ? 'No pods reported' : service.pods!.map((pod) => (
                      <div key={pod.name} className='mb-1'>
                        <span className={pod.ready ? 'text-emerald-600' : 'text-red-600'}>{pod.ready ? 'ready' : 'not ready'}</span>
                        <span className='text-gray-500'> · {pod.name} · restarts {pod.restarts}</span>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
              {!loading && !overviewError && (overview?.services || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className='px-5 py-8 text-center text-sm text-gray-500'>
                    No Kubernetes services reported yet. This endpoint only shows pod health when the report API is running in-cluster with the live health service account.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {overview?.alerts.length ? (
        <div className='rounded-xl border border-amber-200 bg-amber-50 p-5'>
          <h3 className='font-semibold text-gray-900'>Current Alerts</h3>
          <div className='mt-3 space-y-2'>
            {overview.alerts.map((alert, index) => (
              <div key={`${alert.metric}:${index}`} className='text-sm text-gray-800'>
                <span className={alert.severity === 'red' ? 'font-bold text-red-700' : 'font-bold text-amber-700'}>{alert.severity.toUpperCase()}</span>
                <span> · {alert.message} ({alert.value})</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
        <h2 className='text-lg font-semibold text-gray-900'>Session Connection Monitor</h2>
        <div className='mt-4 flex flex-col gap-3 sm:flex-row'>
          <input
            value={sessionInput}
            onChange={(event) => setSessionInput(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') loadSession(sessionInput); }}
            placeholder='Session ID'
            className='flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#F42026] focus:outline-none focus:ring-1 focus:ring-[#F42026]'
          />
          <button
            onClick={() => loadSession(sessionInput)}
            disabled={sessionLoading || !sessionInput.trim()}
            className='rounded-md bg-[#F42026] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d71920] disabled:opacity-50'
          >
            {sessionLoading ? 'Loading...' : 'Inspect'}
          </button>
        </div>

        {overview?.activeSessions.sessions.length ? (
          <div className='mt-5 overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Session</th>
                  <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                  <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Host</th>
                  <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Viewers</th>
                  <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Telemetry</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200 bg-white'>
                {overview.activeSessions.sessions.map((session) => (
                  <tr key={session.sessionId} className='cursor-pointer hover:bg-gray-50' onClick={() => loadSession(session.sessionId)}>
                    <td className='px-4 py-3'>
                      <div className='font-mono text-xs text-gray-900'>{session.sessionId}</div>
                      <div className='mt-1 text-xs text-gray-500'>{session.metadata?.title || session.metadata?.roomName || 'Untitled live'}</div>
                    </td>
                    <td className='px-4 py-3 text-sm text-gray-700'>
                      <div className='font-semibold'>{session.metadata?.lifecycleStatus || 'unknown'}</div>
                      <div className='text-xs text-gray-500'>{session.metadata?.publishStatus || 'unknown'} · {session.metadata?.provider || 'provider unknown'}</div>
                      {session.staleRedisSession ? <div className='text-xs font-semibold text-amber-700'>Redis active, Mongo missing</div> : null}
                    </td>
                    <td className='px-4 py-3 text-sm'>
                      {renderHealthHost(session.metadata, session.hostHeartbeat, session.hostHeartbeatTtl, session.inactiveAt)}
                    </td>
                    <td className='px-4 py-3 text-sm text-gray-700'>{session.viewerCount}</td>
                    <td className='px-4 py-3 text-sm text-gray-700'>{session.telemetryUserCount} users</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='mt-4 rounded-md border border-dashed border-gray-300 px-4 py-6 text-sm text-gray-500'>
            Active sessions will appear here automatically. Click a row to inspect per-user connection telemetry, or paste a known Session ID above.
          </div>
        )}

        {sessionDetail && (
          <div className='mt-6 border-t border-gray-200 pt-5'>
            <div className='flex flex-wrap items-center justify-between gap-3'>
              <h3 className='font-semibold text-gray-900'>Session {sessionDetail.sessionId}</h3>
              <span className='text-sm text-gray-500'>{sessionDetail.viewerCount} viewers · {sessionDetail.telemetry.length} telemetry records</span>
            </div>
            <div className='mt-4 grid grid-cols-1 gap-3 md:grid-cols-3'>
              <SummaryCard label='Lifecycle' value={sessionDetail.metadata?.lifecycleStatus || 'unknown'} tone='text-gray-900' />
              <SummaryCard label='Publish' value={sessionDetail.metadata?.publishStatus || 'unknown'} tone='text-gray-900' />
              <SummaryCard label='Provider' value={sessionDetail.metadata?.provider || 'unknown'} tone='text-gray-900' />
            </div>
            <div className='mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700'>
              <div><span className='font-semibold'>Title:</span> {sessionDetail.metadata?.title || 'Untitled live'}</div>
              <div className='mb-2'>
                <span className='font-semibold'>Host:</span>
                <div className='mt-2'>{renderHealthHost(sessionDetail.metadata, sessionDetail.hostHeartbeat, sessionDetail.hostHeartbeatTtl, sessionDetail.inactiveAt)}</div>
              </div>
              <div><span className='font-semibold'>Room:</span> <span className='font-mono text-xs'>{sessionDetail.metadata?.roomName || 'unknown'}</span></div>
              <div><span className='font-semibold'>Started:</span> {formatDate(sessionDetail.metadata?.startedAt)}</div>
              <div><span className='font-semibold'>Ended:</span> {formatDate(sessionDetail.metadata?.endedAt)}</div>
              {sessionDetail.staleRedisSession ? (
                <div className='mt-2 font-semibold text-amber-700'>This ID is still in Redis active sessions but no Mongo livesession was found.</div>
              ) : null}
            </div>
            <div className='mt-4 overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                    <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>State</th>
                    <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Provider</th>
                    <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Received</th>
                    <th className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Details</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200 bg-white'>
                  {sessionDetail.telemetry.length === 0 ? (
                    <tr>
                      <td colSpan={5} className='px-4 py-6 text-sm text-gray-500'>
                        No client telemetry records for this session. This usually means the viewer/host is on an older mobile build, telemetry has not emitted yet, or the user disconnected more than 5 minutes ago and the Redis telemetry TTL expired.
                      </td>
                    </tr>
                  ) : null}
                  {sessionDetail.telemetry.map((row) => {
                    const displayName = row.user?.name || row.user?.userName || row.user?.email || row.userId;
                    const avatarUrl = row.user?.profileImageThumbnailUrl || row.user?.profileImageUrl || '';
                    return (
                      <tr key={`${row.sessionId}:${row.userId}`}>
                        <td className='px-4 py-3 text-sm text-gray-900'>
                          <div className='flex min-w-[220px] items-center gap-3'>
                            {avatarUrl ? (
                              <img src={avatarUrl} alt='' className='h-9 w-9 rounded-full object-cover' />
                            ) : (
                              <div className='flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600'>
                                {displayName.slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className='font-semibold'>{displayName}</div>
                              {row.user?.userName ? <div className='text-xs text-gray-500'>@{row.user.userName}</div> : null}
                              {row.user?.email ? <div className='text-xs text-gray-500'>{row.user.email}</div> : null}
                              <div className='font-mono text-[11px] text-gray-400'>{row.userId}</div>
                            </div>
                          </div>
                        </td>
                        <td className='px-4 py-3 text-sm font-semibold text-gray-800'>{row.state}</td>
                        <td className='px-4 py-3 text-sm text-gray-700'>
                          <div>{row.provider}</div>
                          <div className='text-xs text-gray-500'>{row.surface}</div>
                          {row.socketId ? <div className='font-mono text-[11px] text-gray-400'>{row.socketId}</div> : null}
                        </td>
                        <td className='px-4 py-3 text-sm text-gray-700'>{formatDate(row.serverReceivedAt)}</td>
                        <td className='px-4 py-3 text-xs text-gray-600'>{renderTelemetryDetails(row.details)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const LiveAccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = useMemo<TabType>(() => {
    if (location.pathname.startsWith('/live-access/config')) return 'config';
    if (location.pathname.startsWith('/live-access/streams')) return 'streams';
    if (location.pathname.startsWith('/live-access/reports')) return 'reports';
    if (location.pathname.startsWith('/live-access/analytics')) return 'analytics';
    if (location.pathname.startsWith('/live-access/health')) return 'health';
    return 'applications';
  }, [location.pathname]);

  const currentSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const routeHostUserId = currentSearchParams.get('hostUserId')?.trim() || '';
  const routeSessionId = currentSearchParams.get('sessionId')?.trim() || '';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [appSearchQuery, setAppSearchQuery] = useState('');
  const appSearchRef = useRef('');
  const [applications, setApplications] = useState<LiveAccessApplication[]>([]);
  const [stats, setStats] = useState<ApplicationStats>(defaultStats);
  const [pagination, setPagination] = useState<PaginationState>(defaultApplicationsPagination);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState('');
  const [applicationLiveCounts, setApplicationLiveCounts] = useState<Record<string, number | null>>({});

  const [config, setConfig] = useState<LiveAccessConfig | null>(null);
  const [draftMode, setDraftMode] = useState<LiveAccessMode>('GLOBAL');
  const [draftAllowlist, setDraftAllowlist] = useState<string[]>([]);
  const [allowlistInput, setAllowlistInput] = useState('');
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const [streamPublishFilter, setStreamPublishFilter] = useState<StreamPublishFilter>('ALL');
  const [streamsHostInput, setStreamsHostInput] = useState(routeHostUserId);
  const [streamRows, setStreamRows] = useState<StreamRow[]>([]);
  const [streamsLoading, setStreamsLoading] = useState(false);
  const [streamsPagination, setStreamsPagination] = useState<PaginationState>(defaultBrowsePagination);

  const [reportsHostInput, setReportsHostInput] = useState(routeHostUserId);
  const [reportsSessionInput, setReportsSessionInput] = useState(routeSessionId);
  const [reportedSessions, setReportedSessions] = useState<LiveReportedSessionSummary[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsPagination, setReportsPagination] = useState<PaginationState>(defaultReportPagination);
  const [topReporters, setTopReporters] = useState<TopReporter[]>([]);

  const [forceApproveInput, setForceApproveInput] = useState('');
  const [forceApproveLoading, setForceApproveLoading] = useState(false);
  const [forceApproveResult, setForceApproveResult] = useState<{ userId: string; userName?: string | null } | null>(null);

  useEffect(() => {
    setStreamsHostInput(routeHostUserId);
    setReportsHostInput(routeHostUserId);
    setReportsSessionInput(routeSessionId);
  }, [routeHostUserId, routeSessionId]);

  const navigateToTab = (tab: TabType) => {
    const nextParams = new URLSearchParams();

    if ((tab === 'streams' || tab === 'reports') && routeHostUserId) {
      nextParams.set('hostUserId', routeHostUserId);
    }

    if (tab === 'reports' && routeSessionId) {
      nextParams.set('sessionId', routeSessionId);
    }

    navigate({
      pathname: tabPaths[tab],
      search: nextParams.toString() ? `?${nextParams.toString()}` : '',
    });
  };

  const loadApplications = useCallback(async (page: number = 1) => {
    setApplicationsLoading(true);
    const search = appSearchRef.current.trim() || undefined;

    try {
      const response = await listLiveAccessApplications({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        search,
        page,
        limit: defaultApplicationsPagination.limit,
      });

      setApplications(response.applications);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load live access applications');
    } finally {
      setApplicationsLoading(false);
    }
  }, [statusFilter]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const [all, pending, approved, rejected, blocked, suspended] = await Promise.all([
        listLiveAccessApplications({ page: 1, limit: 1 }),
        listLiveAccessApplications({ status: 'PENDING', page: 1, limit: 1 }),
        listLiveAccessApplications({ status: 'APPROVED', page: 1, limit: 1 }),
        listLiveAccessApplications({ status: 'REJECTED', page: 1, limit: 1 }),
        listLiveAccessApplications({ status: 'BLOCKED', page: 1, limit: 1 }),
        listLiveAccessApplications({ status: 'SUSPENDED', page: 1, limit: 1 }),
      ]);

      setStats({
        total: all.total,
        pending: pending.total,
        approved: approved.total,
        rejected: rejected.total,
        blocked: blocked.total,
        suspended: suspended.total,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load application stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);

    try {
      const response = await getLiveAccessConfig();
      setConfig(response);
      setDraftMode(response.mode);
      setDraftAllowlist(normalizeUserIds(response.allowlist));
    } catch (error: any) {
      toast.error(error.message || 'Failed to load live access config');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const loadStreams = useCallback(async (page: number = 1) => {
    setStreamsLoading(true);

    try {
      if (routeHostUserId) {
        const response = await listUserLiveSessionsWithReports(routeHostUserId, {
          page,
          limit: defaultBrowsePagination.limit,
        });

        setStreamRows(response.sessions.map(toStreamRowFromReportedSummary));
        setStreamsPagination({
          page: response.page,
          limit: response.limit,
          total: response.total,
          totalPages: Math.max(1, Math.ceil(response.total / Math.max(response.limit, 1))),
        });
        return;
      }

      const response = await listLiveSessions({
        page,
        limit: defaultBrowsePagination.limit,
        ...(streamPublishFilter === 'ALL' ? {} : { publishStatus: streamPublishFilter }),
      });

      setStreamRows(response.sessions.map(toStreamRowFromLiveSession));
      setStreamsPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: Math.max(1, Math.ceil(response.total / Math.max(response.limit, 1))),
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load live streams');
    } finally {
      setStreamsLoading(false);
    }
  }, [routeHostUserId, streamPublishFilter]);

  const loadReportedSessions = useCallback(async (page: number = 1) => {
    setReportsLoading(true);

    try {
      const response = await listReportedLiveSessions({
        ...(routeHostUserId ? { hostUserId: routeHostUserId } : {}),
        ...(routeSessionId ? { sessionId: routeSessionId } : {}),
        page,
        limit: routeHostUserId || routeSessionId ? defaultReportPagination.limit : 100,
      });

      setReportedSessions(response.sessions);
      setReportsPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: Math.max(1, Math.ceil(response.total / Math.max(response.limit, 1))),
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load live reports');
    } finally {
      setReportsLoading(false);
    }
  }, [routeHostUserId, routeSessionId]);

  useEffect(() => {
    loadStats();
    loadConfig();
  }, [loadConfig, loadStats]);

  useEffect(() => {
    if (activeTab === 'applications') {
      loadApplications(1);
    }
  }, [activeTab, loadApplications]);

  useEffect(() => {
    if (activeTab === 'streams') {
      loadStreams(1);
    }
  }, [activeTab, loadStreams]);

  useEffect(() => {
    if (activeTab === 'reports') {
      loadReportedSessions(1);
    }
  }, [activeTab, loadReportedSessions]);

  // Keep ref in sync with state so loadApplications always reads the latest search value
  useEffect(() => { appSearchRef.current = appSearchQuery; });

  // Debounce backend search — fires 500ms after the user stops typing
  useEffect(() => {
    if (activeTab !== 'applications') return;
    const timer = setTimeout(() => { loadApplications(1); }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSearchQuery]);

  // Fetch top reporters whenever the host filter changes in the Reports tab
  useEffect(() => {
    if (!routeHostUserId) { setTopReporters([]); return; }
    getTopReporters(routeHostUserId, 3).then(setTopReporters).catch(() => setTopReporters([]));
  }, [routeHostUserId]);

  useEffect(() => {
    if (activeTab !== 'applications' || applications.length === 0) return;

    const uniqueUserIds = Array.from(new Set(applications.map((application) => application.UserID).filter(Boolean)));
    let cancelled = false;

    setApplicationLiveCounts((current) => {
      const next = { ...current };
      uniqueUserIds.forEach((userId) => {
        if (!(userId in next)) {
          next[userId] = null;
        }
      });
      return next;
    });

    const loadCounts = async () => {
      const entries = await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const response = await listUserLiveSessionsWithReports(userId, { page: 1, limit: 1 });
            return [userId, response.total] as const;
          } catch {
            return [userId, 0] as const;
          }
        }),
      );

      if (cancelled) return;

      setApplicationLiveCounts((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [activeTab, applications]);

  const refreshApplicationsState = useCallback(async (page: number = pagination.page) => {
    await Promise.all([loadStats(), loadApplications(page)]);
  }, [loadApplications, loadStats, pagination.page]);

  const handleReview = async (
    event: React.MouseEvent,
    userId: string,
    status: LiveAccessApplicationStatus,
  ) => {
    event.stopPropagation();

    if (status === 'REJECTED') {
      navigate(`/live-access/${encodeURIComponent(userId)}`);
      return;
    }

    const loadingKey = `${userId}:${status}`;
    setDecisionLoading(loadingKey);

    try {
      await reviewLiveAccessApplication({ userId, status });
      toast.success(`Application ${status.toLowerCase()} successfully`);
      await refreshApplicationsState();
    } catch (error: any) {
      toast.error(error.message || 'Failed to review application');
    } finally {
      setDecisionLoading('');
    }
  };

  const handleAddAllowlistEntry = () => {
    const entries = normalizeUserIds(allowlistInput.split(/[\s,]+/));

    if (entries.length === 0) {
      toast.error('Enter at least one user ID to add');
      return;
    }

    setDraftAllowlist((current) => normalizeUserIds([...current, ...entries]));
    setAllowlistInput('');
  };

  const handleRemoveAllowlistEntry = (userId: string) => {
    setDraftAllowlist((current) => current.filter((item) => item !== userId));
  };

  const handleSaveConfig = async () => {
    if (!config) {
      toast.error('Config not loaded yet');
      return;
    }

    const originalAllowlist = normalizeUserIds(config.allowlist);
    const nextAllowlist = normalizeUserIds(draftAllowlist);
    const addAllowlist = nextAllowlist.filter((userId) => !originalAllowlist.includes(userId));
    const removeAllowlist = originalAllowlist.filter((userId) => !nextAllowlist.includes(userId));

    if (draftMode === config.mode && addAllowlist.length === 0 && removeAllowlist.length === 0) {
      toast.info('No config changes to save');
      return;
    }

    setConfigSaving(true);

    try {
      const response = await updateLiveAccessConfig({
        ...(draftMode !== config.mode ? { mode: draftMode } : {}),
        ...(addAllowlist.length ? { addAllowlist } : {}),
        ...(removeAllowlist.length ? { removeAllowlist } : {}),
      });

      setConfig(response);
      setDraftMode(response.mode);
      setDraftAllowlist(normalizeUserIds(response.allowlist));
      toast.success('Live access config updated');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save live access config');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleResetConfig = () => {
    if (!config) return;
    setDraftMode(config.mode);
    setDraftAllowlist(normalizeUserIds(config.allowlist));
    setAllowlistInput('');
  };

  const applyStreamsFilter = () => {
    const nextParams = new URLSearchParams();
    const trimmed = streamsHostInput.trim();

    if (trimmed) nextParams.set('hostUserId', trimmed);

    navigate({
      pathname: tabPaths.streams,
      search: nextParams.toString() ? `?${nextParams.toString()}` : '',
    });
  };

  const applyReportsFilter = () => {
    const nextParams = new URLSearchParams();
    const trimmedHost = reportsHostInput.trim();
    const trimmedSession = reportsSessionInput.trim();

    if (trimmedHost) nextParams.set('hostUserId', trimmedHost);
    if (trimmedSession) nextParams.set('sessionId', trimmedSession);

    navigate({
      pathname: tabPaths.reports,
      search: nextParams.toString() ? `?${nextParams.toString()}` : '',
    });
  };

  const clearReportsFilter = () => {
    setReportsHostInput('');
    setReportsSessionInput('');
    navigate(tabPaths.reports);
  };

  const clearStreamsFilter = () => {
    setStreamsHostInput('');
    navigate(tabPaths.streams);
  };

  const isConfigDirty = (() => {
    if (!config) return false;
    const originalAllowlist = normalizeUserIds(config.allowlist);
    const nextAllowlist = normalizeUserIds(draftAllowlist);
    return draftMode !== config.mode || JSON.stringify(originalAllowlist) !== JSON.stringify(nextAllowlist);
  })();

  const handleForceApprove = async () => {
    const trimmed = forceApproveInput.trim();
    if (!trimmed) {
      toast.error('Enter a user ID or username');
      return;
    }

    setForceApproveLoading(true);
    setForceApproveResult(null);

    try {
      // Resolve userName → userId if needed via review details
      let userId = trimmed;
      let userName: string | null = null;

      if (!/^[a-f0-9]{24}$/i.test(trimmed)) {
        const cleanedUsername = trimmed.replace(/^@/, '');
        // Look up via review details — will 404 if not found
        const details = await getLiveAccessApplicationReviewDetails(cleanedUsername);
        if (!details.profile?.UserID) {
          throw new Error(`No user found for username "${cleanedUsername}"`);
        }
        userId = details.profile.UserID;
        userName = details.profile?.UserName || null;
      }

      await reviewLiveAccessApplication({ userId, status: 'APPROVED' });
      setForceApproveResult({ userId, userName });
      toast.success(`Application approved for ${userName ? `@${userName}` : userId}`);
      await refreshApplicationsState(1);
    } catch (error: any) {
      toast.error(error.message || 'Failed to force approve');
    } finally {
      setForceApproveLoading(false);
    }
  };

  const STATUS_ORDER: Record<string, number> = { PENDING: 0, REJECTED: 1, APPROVED: 2 };

  const sortedApplications = useMemo(
    () => [...applications].sort((a, b) => (STATUS_ORDER[a.Status] ?? 3) - (STATUS_ORDER[b.Status] ?? 3)),
    [applications], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const reportsSummary = useMemo(() => {
    const totalReports = reportedSessions.reduce((sum, session) => sum + session.reportCount, 0);
    const totalReporters = reportedSessions.reduce((sum, session) => sum + session.reporterCount, 0);

    return { totalReports, totalReporters };
  }, [reportedSessions]);

  const groupedByHost = useMemo(() => {
    if (routeHostUserId) return null;
    const map = new Map<string, {
      hostUserId: string;
      hostName: string | null;
      hostUsername: string | null;
      sessionCount: number;
      totalReports: number;
      lastReportedAt: string | null;
    }>();
    for (const session of reportedSessions) {
      if (!map.has(session.hostUserId)) {
        map.set(session.hostUserId, {
          hostUserId: session.hostUserId,
          hostName: session.host?.Name ?? null,
          hostUsername: session.host?.UserName ?? null,
          sessionCount: 0,
          totalReports: 0,
          lastReportedAt: null,
        });
      }
      const g = map.get(session.hostUserId)!;
      g.sessionCount++;
      g.totalReports += session.reportCount;
      if (!g.lastReportedAt || (session.lastReportedAt && session.lastReportedAt > g.lastReportedAt)) {
        g.lastReportedAt = session.lastReportedAt ?? null;
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (b.lastReportedAt && a.lastReportedAt) return b.lastReportedAt > a.lastReportedAt ? 1 : -1;
      return b.totalReports - a.totalReports;
    });
  }, [reportedSessions, routeHostUserId]);

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900'>Live Access</h1>
            <p className='mt-2 text-gray-600'>
              Moderate live access, review creator sessions, and inspect livestream reports from one workspace.
            </p>
          </div>

          <div className='inline-flex items-center gap-2 self-start rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm'>
            <span className='h-2.5 w-2.5 rounded-full bg-emerald-500' />
            Connected to live.tezda.tech
          </div>
        </div>

        <div className='mb-6 flex flex-wrap gap-3'>
          {([
            { id: 'applications', label: 'Applications' },
            { id: 'config', label: 'Access Config' },
            { id: 'streams', label: 'All Live Streams' },
            { id: 'reports', label: 'Live Reports' },
            { id: 'analytics', label: 'Livestream Analytics' },
            { id: 'health', label: 'Live Health' },
          ] as { id: TabType; label: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => navigateToTab(tab.id)}
              className={`rounded-lg px-5 py-2.5 font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white shadow-md'
                  : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'applications' && (
          <div className='space-y-6'>
            {/* Force Approve Tool */}
            <div className='overflow-hidden rounded-xl border border-amber-200 bg-amber-50 shadow-sm'>
              <div className='border-b border-amber-200 px-5 py-4'>
                <h2 className='text-lg font-semibold text-gray-900'>Force Approve Live Access</h2>
                <p className='text-sm text-amber-700'>
                  Directly approves a user's live access application regardless of eligibility requirements. Use for testing and support only.
                </p>
              </div>

              <div className='px-5 py-5 space-y-4'>
                <div className='flex flex-col gap-3 sm:flex-row'>
                  <input
                    value={forceApproveInput}
                    onChange={(e) => setForceApproveInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleForceApprove(); }}
                    placeholder='User ID or @username'
                    className='flex-1 rounded-lg border border-amber-300 bg-white px-4 py-2.5 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  />
                  <button
                    onClick={handleForceApprove}
                    disabled={forceApproveLoading}
                    className='rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {forceApproveLoading ? 'Approving...' : 'Force Approve'}
                  </button>
                </div>

                {forceApproveResult && (
                  <div className='flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3'>
                    <span className='text-emerald-600 text-lg'>✓</span>
                    <div>
                      <p className='text-sm font-semibold text-emerald-800'>Application approved</p>
                      <p className='text-xs text-emerald-700 font-mono'>
                        {forceApproveResult.userName ? `@${forceApproveResult.userName} · ` : ''}{forceApproveResult.userId}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'>
              <SummaryCard label='Total Applications' value={statsLoading ? '...' : stats.total} />
              <SummaryCard label='Pending' value={statsLoading ? '...' : stats.pending} tone='text-amber-600' />
              <SummaryCard label='Approved' value={statsLoading ? '...' : stats.approved} tone='text-emerald-600' />
              <SummaryCard label='Rejected' value={statsLoading ? '...' : stats.rejected} tone='text-rose-600' />
              <SummaryCard label='Blocked' value={statsLoading ? '...' : stats.blocked} tone='text-orange-600' />
              <SummaryCard label='Suspended' value={statsLoading ? '...' : stats.suspended} tone='text-red-800' />
            </div>

            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>Application Review Queue</h2>
                  <p className='text-sm text-gray-500'>
                    Click a row to view full details. Each row now includes the creator&apos;s livestream count.
                  </p>
                </div>

                <div className='flex flex-wrap items-center gap-3'>
                  <div className='relative'>
                    <svg className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z' />
                    </svg>
                    <input
                      type='text'
                      value={appSearchQuery}
                      onChange={(e) => setAppSearchQuery(e.target.value)}
                      placeholder='Search user ID or username…'
                      className='w-64 rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-[#FF9C15] focus:outline-none'
                    />
                    {appSearchQuery && (
                      <button
                        onClick={() => setAppSearchQuery('')}
                        className='absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      >
                        <svg className='h-3.5 w-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                        </svg>
                      </button>
                    )}
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  >
                    <option value='ALL'>All Statuses</option>
                    <option value='PENDING'>Pending</option>
                    <option value='APPROVED'>Approved</option>
                    <option value='REJECTED'>Rejected</option>
                    <option value='BLOCKED'>Blocked</option>
                    <option value='SUSPENDED'>Suspended</option>
                  </select>

                  <button
                    onClick={() => refreshApplicationsState(1)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div>
                <table className='w-full table-fixed divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th style={{width:'15%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User ID</th>
                      <th style={{width:'11%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Username</th>
                      <th style={{width:'6%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Streams</th>
                      <th style={{width:'9%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                      <th style={{width:'13%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Applied At</th>
                      <th style={{width:'13%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reviewed At</th>
                      <th style={{width:'13%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Note</th>
                      <th style={{width:'20%'}} className='px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 bg-white'>
                    {applicationsLoading && (
                      <tr>
                        <td className='px-3 py-6 text-sm text-gray-500' colSpan={8}>
                          Loading applications...
                        </td>
                      </tr>
                    )}

                    {!applicationsLoading && sortedApplications.length === 0 && (
                      <tr>
                        <td className='px-3 py-6 text-sm text-gray-500' colSpan={8}>
                          {appSearchQuery ? `No results for "${appSearchQuery}".` : 'No applications found for the selected filter.'}
                        </td>
                      </tr>
                    )}

                    {!applicationsLoading && sortedApplications.map((application) => {
                      const isApproveLoading = decisionLoading === `${application.UserID}:APPROVED`;
                      const isRejectLoading = decisionLoading === `${application.UserID}:REJECTED`;
                      const liveCount = applicationLiveCounts[application.UserID];

                      return (
                        <tr
                          key={`${application.UserID}-${application.AppliedAt || application.ReviewedAt || application.Status}`}
                          onClick={() => navigate(`/live-access/${encodeURIComponent(application.UserID)}`)}
                          className='cursor-pointer transition-colors hover:bg-orange-50/50'
                        >
                          <td className='px-3 py-4 text-sm font-medium text-gray-900'>
                            <span className='block truncate font-mono text-xs' title={application.UserID}>{application.UserID}</span>
                          </td>
                          <td className='px-3 py-4 text-sm text-gray-700'>
                            <span className='block truncate'>
                              {application.UserName ? (
                                <span className='font-medium text-gray-900'>@{application.UserName}</span>
                              ) : (
                                <span className='text-gray-400 italic'>—</span>
                              )}
                            </span>
                          </td>
                          <td className='px-3 py-4 text-sm text-gray-700'>
                            <span className='inline-flex rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-700'>
                              {liveCount === null || liveCount === undefined ? '...' : liveCount}
                            </span>
                          </td>
                          <td className='px-3 py-4 text-sm'>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(application.Status)}`}>
                              {application.Status}
                            </span>
                          </td>
                          <td className='px-3 py-4 text-xs text-gray-700'>{formatDate(application.AppliedAt)}</td>
                          <td className='px-3 py-4 text-xs text-gray-700'>{formatDate(application.ReviewedAt)}</td>
                          <td className='px-3 py-4 text-sm text-gray-700'>
                            <div className='overflow-x-auto whitespace-nowrap' title={application.Note || ''}>
                              <span className='text-xs'>{application.Note || 'N/A'}</span>
                            </div>
                          </td>
                          <td className='px-3 py-4 text-sm'>
                            <div className='flex items-center gap-1.5'>
                              {application.Status !== 'APPROVED' && (
                                <button
                                  onClick={(e) => handleReview(e, application.UserID, 'APPROVED')}
                                  disabled={Boolean(decisionLoading)}
                                  className='rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                                >
                                  {isApproveLoading ? '…' : 'Approve'}
                                </button>
                              )}
                              {application.Status !== 'REJECTED' && (
                                <button
                                  onClick={(e) => handleReview(e, application.UserID, 'REJECTED')}
                                  disabled={Boolean(decisionLoading)}
                                  className='rounded-md bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                >
                                  {isRejectLoading ? '…' : 'Reject'}
                                </button>
                              )}
                              {application.Status === 'APPROVED' && (
                                <button
                                  onClick={(e) => handleReview(e, application.UserID, 'REJECTED')}
                                  disabled={Boolean(decisionLoading)}
                                  className='rounded-md border border-rose-300 px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60'
                                >
                                  {isRejectLoading ? '…' : 'Revoke'}
                                </button>
                              )}
                              {application.Status === 'REJECTED' && (
                                <button
                                  onClick={(e) => handleReview(e, application.UserID, 'APPROVED')}
                                  disabled={Boolean(decisionLoading)}
                                  className='rounded-md border border-emerald-300 px-2.5 py-1.5 text-xs font-semibold text-emerald-600 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                                >
                                  {isApproveLoading ? '…' : 'Approve'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
                <p className='text-sm text-gray-600'>
                  Page {pagination.page} of {Math.max(pagination.totalPages, 1)} | Total {pagination.total}
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => loadApplications(Math.max(1, pagination.page - 1))}
                    disabled={pagination.page <= 1 || applicationsLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadApplications(Math.min(Math.max(pagination.totalPages, 1), pagination.page + 1))}
                    disabled={pagination.page >= Math.max(pagination.totalPages, 1) || applicationsLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'config' && (
          <div className='space-y-6'>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
              <SummaryCard label='Current Mode' value={configLoading ? '...' : config?.mode || 'GLOBAL'} />
              <SummaryCard label='Allowlist Users' value={configLoading ? '...' : draftAllowlist.length} />
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Last Updated</p>
                <p className='mt-1 text-lg font-semibold text-gray-900'>{configLoading ? '...' : formatDate(config?.updatedAt)}</p>
                <p className='mt-1 text-sm text-gray-500'>By {config?.updatedBy || 'N/A'}</p>
              </div>
            </div>

            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='border-b border-gray-200 px-5 py-4'>
                <h2 className='text-lg font-semibold text-gray-900'>Access Configuration</h2>
                <p className='text-sm text-gray-500'>
                  GLOBAL enforces the production eligibility flow. ALLOWLIST bypasses checks for listed user IDs.
                </p>
              </div>

              <div className='space-y-6 px-5 py-5'>
                <div>
                  <p className='mb-3 text-sm font-semibold text-gray-700'>Mode</p>
                  <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                    {([
                      {
                        mode: 'GLOBAL',
                        label: 'GLOBAL',
                        description: 'Use full live eligibility rules and approved applications.',
                      },
                      {
                        mode: 'ALLOWLIST',
                        label: 'ALLOWLIST',
                        description: 'Allow only explicit user IDs in the allowlist.',
                      },
                    ] as { mode: LiveAccessMode; label: string; description: string }[]).map((option) => (
                      <button
                        key={option.mode}
                        onClick={() => setDraftMode(option.mode)}
                        className={`rounded-xl border p-4 text-left transition-all ${
                          draftMode === option.mode
                            ? 'border-[#FF9C15] bg-orange-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className='flex items-center justify-between'>
                          <span className='text-base font-semibold text-gray-900'>{option.label}</span>
                          {draftMode === option.mode && (
                            <span className='rounded-full bg-[#FF9C15] px-2 py-0.5 text-xs font-semibold text-white'>
                              Selected
                            </span>
                          )}
                        </div>
                        <p className='mt-2 text-sm text-gray-600'>{option.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className='mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
                    <div>
                      <p className='text-sm font-semibold text-gray-700'>Allowlist</p>
                      <p className='text-sm text-gray-500'>Add one or many user IDs separated by comma or whitespace.</p>
                    </div>
                    <button
                      onClick={loadConfig}
                      className='self-start rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50'
                    >
                      Reload from server
                    </button>
                  </div>

                  <div className='flex flex-col gap-3 sm:flex-row'>
                    <input
                      value={allowlistInput}
                      onChange={(event) => setAllowlistInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddAllowlistEntry();
                        }
                      }}
                      placeholder='Enter user IDs'
                      className='flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                    />
                    <button
                      onClick={handleAddAllowlistEntry}
                      className='rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800'
                    >
                      Add User IDs
                    </button>
                  </div>

                  <div className='mt-4 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4'>
                    {draftAllowlist.length === 0 ? (
                      <p className='text-sm text-gray-500'>No user IDs in the allowlist.</p>
                    ) : (
                      <div className='flex flex-wrap gap-2'>
                        {draftAllowlist.map((userId) => (
                          <span
                            key={userId}
                            className='inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-medium text-orange-700'
                          >
                            {userId}
                            <button
                              onClick={() => handleRemoveAllowlistEntry(userId)}
                              className='text-orange-500 hover:text-orange-700'
                              aria-label={`Remove ${userId}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className='flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4'>
                  <div className='text-sm text-gray-500'>
                    {isConfigDirty ? 'You have unsaved config changes.' : 'Config is in sync with the server.'}
                  </div>

                  <div className='flex items-center gap-3'>
                    <button
                      onClick={handleResetConfig}
                      disabled={!isConfigDirty || configSaving}
                      className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      Reset
                    </button>
                    <button
                      onClick={handleSaveConfig}
                      disabled={!isConfigDirty || configSaving}
                      className='rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-5 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      {configSaving ? 'Saving...' : 'Save Config'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'streams' && (
          <div className='space-y-6'>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
              <SummaryCard label={routeHostUserId ? 'User Sessions' : 'Visible Streams'} value={streamsLoading ? '...' : streamsPagination.total} />
              <SummaryCard label='Showing For Host' value={routeHostUserId || 'All hosts'} tone='text-blue-600' />
              <SummaryCard label='Mode' value={routeHostUserId ? 'Host History' : 'Live Discovery'} tone='text-emerald-600' />
            </div>

            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 lg:flex-row lg:items-center lg:justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>
                    {routeHostUserId ? 'Host Livestream Sessions' : 'All Live Streams'}
                  </h2>
                  <p className='text-sm text-gray-500'>
                    {routeHostUserId
                      ? 'Showing all sessions created by the selected host, including report counts.'
                      : 'Browse currently STARTING or LIVE sessions across all users.'}
                  </p>
                </div>

                <div className='flex flex-col gap-3 md:flex-row md:items-center'>
                  <input
                    value={streamsHostInput}
                    onChange={(event) => setStreamsHostInput(event.target.value)}
                    placeholder='Filter by host user ID'
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  />
                  {!routeHostUserId && (
                    <select
                      value={streamPublishFilter}
                      onChange={(event) => setStreamPublishFilter(event.target.value as StreamPublishFilter)}
                      className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                    >
                      <option value='ALL'>All publish states</option>
                      <option value='PUBLISHED'>Published only</option>
                      <option value='UNPUBLISHED'>Unpublished only</option>
                    </select>
                  )}
                  <button
                    onClick={applyStreamsFilter}
                    className='rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
                  >
                    Apply
                  </button>
                  {(routeHostUserId || streamPublishFilter !== 'ALL') && (
                    <button
                      onClick={clearStreamsFilter}
                      className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {streamsLoading ? (
                <div className='px-5 py-12 text-center text-sm text-gray-500'>Loading live streams...</div>
              ) : streamRows.length === 0 ? (
                <div className='px-5 py-12 text-center text-sm text-gray-500'>No livestream sessions found.</div>
              ) : (
                <div className='grid gap-5 p-5 md:grid-cols-2 xl:grid-cols-3'>
                  {streamRows.map((stream) => (
                    <div key={stream.sessionId} className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
                      <div className='h-48 w-full bg-gradient-to-br from-gray-100 to-gray-200'>
                        {stream.coverUrl ? (
                          <img src={stream.coverUrl} alt={stream.title} className='h-full w-full object-cover' />
                        ) : (
                          <div className='flex h-full items-center justify-center px-6 text-center text-sm text-gray-500'>
                            No cover available
                          </div>
                        )}
                      </div>

                      <div className='p-5'>
                        <div className='flex flex-wrap items-start justify-between gap-3'>
                          <div className='min-w-0'>
                            <h3 className='truncate text-base font-semibold text-gray-900'>{stream.title}</h3>
                            <p className='mt-1 text-sm text-gray-500'>
                              Host: {stream.hostName || stream.hostUserId}
                            </p>
                            <p className='mt-1 text-xs font-mono text-gray-400'>{stream.sessionId}</p>
                          </div>
                          <div className='flex flex-wrap gap-2'>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(stream.lifecycleStatus)}`}>
                              {stream.lifecycleStatus}
                            </span>
                            {stream.publishStatus && (
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(stream.publishStatus)}`}>
                                {stream.publishStatus}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className='mt-4 grid grid-cols-2 gap-3 text-sm text-gray-600'>
                          <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
                            <p className='text-xs uppercase tracking-wide text-gray-400'>Started</p>
                            <p className='mt-1 font-medium text-gray-900'>{formatDate(stream.startedAt)}</p>
                          </div>
                          <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
                            <p className='text-xs uppercase tracking-wide text-gray-400'>Ended</p>
                            <p className='mt-1 font-medium text-gray-900'>{formatDate(stream.endedAt)}</p>
                          </div>
                          <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
                            <p className='text-xs uppercase tracking-wide text-gray-400'>Reports</p>
                            <p className='mt-1 font-medium text-gray-900'>{stream.reportCount ?? 'N/A'}</p>
                          </div>
                          <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'>
                            <p className='text-xs uppercase tracking-wide text-gray-400'>Reporters</p>
                            <p className='mt-1 font-medium text-gray-900'>{stream.reporterCount ?? 'N/A'}</p>
                          </div>
                        </div>

                        <div className='mt-4 flex flex-wrap gap-3'>
                          <button
                            onClick={() => navigate(`/live-access/sessions/${encodeURIComponent(stream.sessionId)}`)}
                            className='rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95'
                          >
                            View Session
                          </button>
                          <button
                            onClick={() => navigate(`/live-access/reports/${encodeURIComponent(stream.sessionId)}`)}
                            className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                          >
                            View Reports
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
                <p className='text-sm text-gray-600'>
                  Page {streamsPagination.page} of {Math.max(streamsPagination.totalPages, 1)} | Total {streamsPagination.total}
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => loadStreams(Math.max(1, streamsPagination.page - 1))}
                    disabled={streamsPagination.page <= 1 || streamsLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadStreams(Math.min(Math.max(streamsPagination.totalPages, 1), streamsPagination.page + 1))}
                    disabled={streamsPagination.page >= Math.max(streamsPagination.totalPages, 1) || streamsLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <LivestreamAnalyticsTab />
        )}

        {activeTab === 'health' && (
          <LiveHealthTab />
        )}

        {activeTab === 'reports' && (
          <div className='space-y-6'>
            <div className={`grid grid-cols-1 gap-4 ${routeHostUserId ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}>
              {!routeHostUserId ? (
                <>
                  <SummaryCard label='Reported Users' value={reportsLoading ? '...' : (groupedByHost?.length ?? 0)} tone='text-rose-600' />
                  <SummaryCard label='Reported Sessions' value={reportsLoading ? '...' : reportsPagination.total} />
                  <SummaryCard label='Total Reports' value={reportsLoading ? '...' : reportsSummary.totalReports} />
                </>
              ) : (
                <>
                  <SummaryCard label='Reported Sessions' value={reportsLoading ? '...' : reportsPagination.total} tone='text-rose-600' />
                  <SummaryCard label='Reports On Page' value={reportsLoading ? '...' : reportsSummary.totalReports} />
                  <SummaryCard label='Distinct Reporters On Page' value={reportsLoading ? '...' : reportsSummary.totalReporters} tone='text-blue-600' />
                  <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                    <p className='text-sm text-gray-500'>Top Reporters</p>
                    {topReporters.length === 0 ? (
                      <p className='mt-2 text-sm text-gray-400'>No data</p>
                    ) : (
                      <ol className='mt-2 space-y-2'>
                        {topReporters.map((r, i) => (
                          <li key={r.reporterUserId} className='flex items-center gap-2'>
                            <span className='w-4 text-xs font-semibold text-gray-400'>#{i + 1}</span>
                            <div className='min-w-0 flex-1'>
                              <p className='truncate text-sm font-semibold text-gray-900'>
                                {r.reporterUserName ? `@${r.reporterUserName}` : 'Unknown'}
                              </p>
                              <p className='truncate font-mono text-xs text-gray-400'>{r.reporterUserId}</p>
                            </div>
                            <span className='text-sm font-bold text-rose-600'>{r.reportCount}×</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 lg:flex-row lg:items-center lg:justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>
                    {routeHostUserId ? 'Reported Sessions for Host' : 'Live Report Queue'}
                  </h2>
                  <p className='text-sm text-gray-500'>
                    {routeHostUserId
                      ? 'All reported sessions from this host. Click a row to view full reports.'
                      : 'One row per user — click a user to see all their reported sessions.'}
                  </p>
                </div>

                <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-4'>
                  <input
                    value={reportsHostInput}
                    onChange={(event) => setReportsHostInput(event.target.value)}
                    placeholder='Filter by host user ID'
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  />
                  <input
                    value={reportsSessionInput}
                    onChange={(event) => setReportsSessionInput(event.target.value)}
                    placeholder='Filter by session ID'
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  />
                  <button
                    onClick={applyReportsFilter}
                    className='rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800'
                  >
                    Apply
                  </button>
                  <button
                    onClick={clearReportsFilter}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className='overflow-x-auto'>
                {!routeHostUserId ? (
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Sessions Reported</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Total Reports</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Last Reported</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100 bg-white'>
                      {reportsLoading && (
                        <tr>
                          <td className='px-5 py-6 text-sm text-gray-500' colSpan={5}>Loading live reports...</td>
                        </tr>
                      )}
                      {!reportsLoading && (groupedByHost?.length ?? 0) === 0 && (
                        <tr>
                          <td className='px-5 py-6 text-sm text-gray-500' colSpan={5}>No reported users found.</td>
                        </tr>
                      )}
                      {!reportsLoading && groupedByHost?.map((group) => (
                        <tr
                          key={group.hostUserId}
                          onClick={() => navigate(`/live-access/reports?hostUserId=${encodeURIComponent(group.hostUserId)}`)}
                          className='cursor-pointer transition-colors hover:bg-orange-50/50'
                        >
                          <td className='px-5 py-4 text-sm'>
                            <div>
                              <p className='font-semibold text-gray-900'>
                                {group.hostName || (group.hostUsername ? `@${group.hostUsername}` : group.hostUserId)}
                              </p>
                              {group.hostName && group.hostUsername && (
                                <p className='text-xs text-gray-500'>@{group.hostUsername}</p>
                              )}
                              <p className='mt-0.5 text-xs font-mono text-gray-400'>{group.hostUserId}</p>
                            </div>
                          </td>
                          <td className='px-5 py-4 text-sm font-semibold text-gray-700'>{group.sessionCount}</td>
                          <td className='px-5 py-4 text-sm font-semibold text-rose-600'>{group.totalReports}</td>
                          <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(group.lastReportedAt)}</td>
                          <td className='px-5 py-4 text-sm'>
                            <div className='flex flex-wrap gap-2'>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/live-access/reports?hostUserId=${encodeURIComponent(group.hostUserId)}`); }}
                                className='rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800'
                              >
                                View Sessions
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/live-access/${encodeURIComponent(group.hostUserId)}`); }}
                                className='rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50'
                              >
                                Application
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Session</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reports</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reporters</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Last Reported</th>
                        <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100 bg-white'>
                      {reportsLoading && (
                        <tr>
                          <td className='px-5 py-6 text-sm text-gray-500' colSpan={6}>Loading live reports...</td>
                        </tr>
                      )}
                      {!reportsLoading && reportedSessions.length === 0 && (
                        <tr>
                          <td className='px-5 py-6 text-sm text-gray-500' colSpan={6}>No reported livestreams found.</td>
                        </tr>
                      )}
                      {!reportsLoading && reportedSessions.map((session) => (
                        <tr
                          key={session.sessionId}
                          className='cursor-pointer transition-colors hover:bg-orange-50/50'
                          onClick={() => navigate(`/live-access/reports/${encodeURIComponent(session.sessionId)}`)}
                        >
                          <td className='px-5 py-4 text-sm text-gray-900'>
                            <div className='min-w-[200px]'>
                              <p className='font-semibold text-gray-900'>{session.sessionTitle || 'Untitled live'}</p>
                              <p className='mt-1 text-xs font-mono text-gray-400'>{session.sessionId}</p>
                            </div>
                          </td>
                          <td className='px-5 py-4 text-sm'>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(session.sessionLifecycleStatus)}`}>
                              {session.sessionLifecycleStatus}
                            </span>
                          </td>
                          <td className='px-5 py-4 text-sm font-semibold text-rose-600'>{session.reportCount}</td>
                          <td className='px-5 py-4 text-sm font-semibold text-blue-600'>{session.reporterCount}</td>
                          <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(session.lastReportedAt)}</td>
                          <td className='px-5 py-4 text-sm'>
                            <div className='flex flex-wrap gap-2'>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/live-access/reports/${encodeURIComponent(session.sessionId)}`); }}
                                className='rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800'
                              >
                                View Reports
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/live-access/sessions/${encodeURIComponent(session.sessionId)}`); }}
                                className='rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50'
                              >
                                Session Detail
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {routeHostUserId && (
                <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
                  <p className='text-sm text-gray-600'>
                    Page {reportsPagination.page} of {Math.max(reportsPagination.totalPages, 1)} | Total {reportsPagination.total}
                  </p>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={() => loadReportedSessions(Math.max(1, reportsPagination.page - 1))}
                      disabled={reportsPagination.page <= 1 || reportsLoading}
                      className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => loadReportedSessions(Math.min(Math.max(reportsPagination.totalPages, 1), reportsPagination.page + 1))}
                      disabled={reportsPagination.page >= Math.max(reportsPagination.totalPages, 1) || reportsLoading}
                      className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveAccess;
